// Pure scrubbing logic for Sentry payloads. Deliberately free of imports and
// side effects so it can be unit-tested directly — this is the code that
// enforces the privacy contract, so it should be verifiable without booting
// the SDK. See src/sentry.js for where these are wired in.

// Query strings can carry tokens; we only ever want the path.
export const stripQuery = (url) => {
  if (typeof url !== 'string') return url;
  const q = url.indexOf('?');
  return q === -1 ? url : url.slice(0, q);
};

// Runs on every outgoing event, including native crashes forwarded from iOS.
export const scrubEvent = (event) => {
  if (!event) return event;

  if (event.request) {
    // The body. Coach messages, journal text and Plaid payloads all travel
    // here. Deleted unconditionally rather than by endpoint denylist, so a
    // future integration cannot quietly start sending it.
    delete event.request.data;
    delete event.request.cookies;
    delete event.request.headers; // carries the Supabase bearer token
    delete event.request.query_string;
    if (event.request.url) event.request.url = stripQuery(event.request.url);
  }

  // Identity: Supabase user id and nothing else. Anything Sentry inferred —
  // email, username, ip_address — is dropped.
  if (event.user) {
    const id = event.user.id;
    if (id) event.user = { id };
    else delete event.user;
  }

  // User-typed text never leaves the device in an event: habit notes, journal
  // and mood notes travel under these keys when a row is attached as context.
  for (const bag of [event.extra, event.contexts]) {
    if (!bag || typeof bag !== 'object') continue;
    for (const k of Object.keys(bag)) {
      if (/^(note|notes|text|journal)$/i.test(k)) delete bag[k];
      else if (bag[k] && typeof bag[k] === 'object') for (const kk of Object.keys(bag[k])) if (/^(note|notes|text|journal)$/i.test(kk)) delete bag[k][kk];
    }
  }

  // Short, readable code shown on the error screen. Searchable in Sentry as
  // `short_id:ABC12345`, which a bare event-id prefix is not.
  if (event.event_id) {
    event.tags = { ...event.tags, short_id: event.event_id.slice(0, 8).toUpperCase() };
  }
  return event;
};

// Breadcrumbs are the real leak path, not events: the console integration
// serialises every console argument, and this app logs whole rows (see
// healthSync's "mapped row count ... first row" log). So we keep only
// warn/error console breadcrumbs — developer strings that are useful for
// debugging — and discard the serialised arguments beside them.
export const scrubBreadcrumb = (breadcrumb) => {
  if (!breadcrumb) return breadcrumb;
  if (breadcrumb.category === 'console') {
    if (breadcrumb.level !== 'error' && breadcrumb.level !== 'warning') return null;
    if (breadcrumb.data) delete breadcrumb.data.arguments;
  }
  // fetch/xhr breadcrumbs record url + method + status, never the body.
  if (breadcrumb.data && breadcrumb.data.url) {
    breadcrumb.data.url = stripQuery(breadcrumb.data.url);
  }
  return breadcrumb;
};
